import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useTheme, useMediaQuery, Box, styled, LinearProgress, linearProgressClasses } from "@mui/material";
import { useState } from "react";
import MessageCard from "../../Components/Card/MessageCard.js";
import { formatMoney } from "../../utils/stringUtils.js";
import { sumAllItemQuantities } from "../../utils/checkout.js";
import pluralize from "pluralize";
import { brandclubStyles } from "../../styles/index.js";
import { CartProduct } from "./CartProduct.js";
import { createResponsiveStyles } from "../../theme/util.js";
import { ButtonName, DTCComponentName } from "../../theme/componentNames.js";
import { CloseButton } from "../../Components/Button/CloseButton.js";
import { Button } from "../../Components/Button/Button.js";
const StyledMesssageCard = styled(MessageCard)(({ theme }) => {
  var _a;
  return {
    color: theme.vars.palette.text.primary,
    ...createResponsiveStyles(theme, (_a = theme.componentCSSOverrides) == null ? void 0 : _a[DTCComponentName.EmptyBagMessageCard]),
    ".store-message-card__cta": {
      backgroundColor: theme.vars.palette.primary.main,
      color: theme.vars.palette.primary.contrastText
    }
  };
});
const EmptyBagMessageCard = ({ onActionClick }) => {
  var _a, _b;
  const theme = useTheme();
  const customHeaderText = (_b = (_a = theme.globalComponentPresets) == null ? void 0 : _a.checkoutDrawer) == null ? void 0 : _b.customEmptyBagMessageCardHeaderText;
  return /* @__PURE__ */ jsx(Box, { sx: { padding: "20px" }, children: /* @__PURE__ */ jsx(
    StyledMesssageCard,
    {
      title: customHeaderText || "Your bag is empty.",
      content: "Can we make some suggestions to help get you started?",
      cta: "Shop popular products",
      onActionClick
    }
  ) });
};
const getTotal = (products) => {
  return products.reduce((acc, item) => {
    return acc + item.retailPrice * item.quantity;
  }, 0);
};
const calculateTotalOfAllCarts = (carts) => {
  const total = carts.reduce((acc, cart) => {
    return acc + getTotal(cart.items ?? []);
  }, 0);
  return formatMoney(total);
};
const CheckoutContainer = styled("div", {
  label: "CheckoutContainer"
})(({ theme }) => {
  var _a, _b, _c, _d;
  return {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    backgroundColor: theme.vars.palette.background.paper,
    width: 462,
    maxWidth: "100vw",
    color: theme.vars.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    ".bc_header_section": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "23px 28px 10px 28px",
      [theme.breakpoints.down("sm")]: {
        padding: "23px 5% 10px 5%"
      },
      ".bc_header": {
        fontSize: 20,
        fontWeight: 700
      },
      ".sub_header": {
        fontSize: 18,
        fontWeight: 600
      }
    },
    ".product_section": {
      flex: 1,
      overflowY: "scroll",
      ...brandclubStyles.customScrollbar,
      padding: "23px 27px",
      ".cart_product + .cart_product": {
        borderTop: `1px solid #dcdcdc`
      },
      [theme.breakpoints.down("sm")]: {
        padding: "23px 5%"
      },
      ".cart_group": {
        marginBottom: 50,
        [theme.breakpoints.down("sm")]: {
          marginBottom: 30
        }
      },
      ".group_info": {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 8,
        marginBottom: 10,
        [theme.breakpoints.down("sm")]: {
          marginTop: 10,
          marginBottom: 14
        },
        ".group_count": {
          fontSize: 14,
          fontWeight: 500
        },
        ".group_subtotal": {
          fontSize: 14,
          fontWeight: 500,
          ".bc-dtc-price, .price": {
            fontWeight: 600
          }
        }
      }
    },
    ".checkout_section": {
      display: "flex",
      flexDirection: "column",
      gap: "23px",
      padding: "23px 27px 37px 27px",
      boxShadow: "0 0 6px 0 rgba(119, 115, 115, 0.16)",
      [theme.breakpoints.down("sm")]: {
        padding: ((_b = (_a = theme.globalComponentPresets) == null ? void 0 : _a.checkoutDrawer) == null ? void 0 : _b.checkoutDrawerHeaderButton) ? "23px 5% 23px 5%" : "23px 5% 37px 5%",
        boxShadow: ((_d = (_c = theme.globalComponentPresets) == null ? void 0 : _c.checkoutDrawer) == null ? void 0 : _d.checkoutDrawerHeaderButton) ? `0 4px 6px -2px rgba(119, 115, 115, 0.16)` : "0 0 6px 0 rgba(119, 115, 115, 0.16)"
      },
      ".subtotal": {
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        fontSize: 18,
        fontWeight: 600,
        color: theme.vars.palette.text.primary
      }
    }
  };
});
const StyledEmptyBagMessageCard = styled(EmptyBagMessageCard)({
  width: "auto",
  margin: "0 25px",
  marginTop: 50,
  ".store-message-card__content": {
    fontSize: 16,
    width: 282
  },
  ".store-message-card__title": {
    fontSize: 20
  },
  ".store-message-card__cta": {
    marginTop: 10
  }
});
const getAllSortedProducts = (cartItems, allCarts) => {
  const allItems = allCarts.flatMap((cart) => cart.items);
  const sortedProducts = allItems.sort((a, b) => {
    var _a, _b;
    const aCreatedTime = (_a = cartItems.find((item) => item.retailerSku === a.retailerSku)) == null ? void 0 : _a.createdTime;
    const bCreatedTime = (_b = cartItems.find((item) => item.retailerSku === b.retailerSku)) == null ? void 0 : _b.createdTime;
    if (!aCreatedTime && !bCreatedTime) return 0;
    if (!aCreatedTime) return -1;
    if (!bCreatedTime) return 1;
    return bCreatedTime - aCreatedTime;
  });
  return sortedProducts;
};
const ProductSection = ({
  loading,
  allCarts,
  cartItems,
  LoadingComponent,
  storeBrandingType,
  cartProducts,
  handleAddQuantity,
  handleMinusQuantity
}) => {
  if (loading) {
    return /* @__PURE__ */ jsx(Box, { className: "product_section", children: LoadingComponent ? /* @__PURE__ */ jsx(LoadingComponent, {}) : /* @__PURE__ */ jsx("div", { children: "Loading..." }) });
  }
  if (!allCarts || allCarts.length === 0) {
    return null;
  }
  const sortedProducts = getAllSortedProducts(cartItems, allCarts);
  return /* @__PURE__ */ jsxs(Box, { className: "product_section", children: [
    /* @__PURE__ */ jsxs(Box, { className: "group_info", children: [
      /* @__PURE__ */ jsx(Box, { className: "group_count", children: pluralize("item", sumAllItemQuantities(allCarts), true) }),
      /* @__PURE__ */ jsxs(Box, { className: "group_subtotal", children: [
        "Subtotal: ",
        /* @__PURE__ */ jsx("span", { className: "bc-dtc-price", children: calculateTotalOfAllCarts(allCarts) })
      ] })
    ] }),
    sortedProducts.map((product) => /* @__PURE__ */ jsx(
      CartProduct,
      {
        product,
        cartProducts,
        storeBrandingType,
        handleAddQuantity,
        handleMinusQuantity
      },
      `product:${product.stacklineSku}`
    ))
  ] });
};
const CheckoutSection = ({
  initiating,
  closeDrawer,
  canCheckout,
  allCarts = [],
  onCheckout,
  onViewBag,
  closeAfterCheckout = true
}) => {
  var _a, _b, _c, _d;
  const theme = useTheme();
  const customCheckoutText = (_b = (_a = theme.globalComponentPresets) == null ? void 0 : _a.checkoutDrawer) == null ? void 0 : _b.customCheckoutText;
  const customViewBagText = (_d = (_c = theme.globalComponentPresets) == null ? void 0 : _c.checkoutDrawer) == null ? void 0 : _d.customViewBagText;
  const [localInitiating, setLocalInitiating] = useState(false);
  const handleCheckout = async () => {
    if (allCarts.length > 0) {
      try {
        setLocalInitiating(true);
        await onCheckout(allCarts);
      } finally {
        if (closeAfterCheckout) {
          closeDrawer();
        }
      }
    }
  };
  return /* @__PURE__ */ jsx(Box, { className: "checkout_section", children: /* @__PURE__ */ jsxs(Box, { display: "flex", gap: "12px", width: "100%", flexDirection: "column", alignItems: "center", children: [
    canCheckout ? /* @__PURE__ */ jsx(
      Button,
      {
        className: "bc-checkout-drawer-checkout-button",
        increaseSpecificity: true,
        variant: "solid",
        color: "primary",
        fullWidth: true,
        buttonName: ButtonName.BagDrawerCheckoutButton,
        disabled: initiating || localInitiating,
        onClick: handleCheckout,
        disableMaxWidth: true,
        sx: {
          width: "100%"
        },
        children: customCheckoutText || "Checkout"
      }
    ) : null,
    /* @__PURE__ */ jsx(
      Button,
      {
        className: "bc-checkout-drawer-view-bag-button",
        increaseSpecificity: true,
        fullWidth: true,
        variant: "outline",
        color: "primary",
        buttonName: ButtonName.BagDrawerViewBagButton,
        onClick: onViewBag,
        disableMaxWidth: true,
        sx: {
          width: "100%"
        },
        children: customViewBagText || "View bag"
      }
    )
  ] }) });
};
const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200]
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.vars.palette.primary.main
  }
}));
const StyledFreeShippingCap = styled(Box)(({ theme }) => ({
  color: theme.vars.palette.text.primary,
  padding: "23px 27px 0 27px",
  boxSizing: "border-box",
  [theme.breakpoints.down("sm")]: {
    padding: "23px 5%"
  },
  ".free-shipping-cap-text": {
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 6
  },
  ".free-shipping-text": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 12,
    fontWeight: 500,
    color: theme.vars.palette.text.primary
  }
}));
const FreeShippingOffer = ({
  allCarts,
  freeShippingCap
}) => {
  const total = (allCarts == null ? void 0 : allCarts.reduce((acc, cart) => {
    return acc + getTotal(cart.items ?? []);
  }, 0)) || 0;
  const isFreeShipping = total >= freeShippingCap;
  const remainingAmount = freeShippingCap - total;
  if (total === 0) {
    return null;
  }
  if (isFreeShipping) {
    return /* @__PURE__ */ jsx(StyledFreeShippingCap, { children: /* @__PURE__ */ jsx(Box, { className: "free-shipping-cap-text", children: "You're getting Free Standard Shipping!" }) });
  }
  return /* @__PURE__ */ jsxs(StyledFreeShippingCap, { children: [
    /* @__PURE__ */ jsxs(Box, { className: "free-shipping-cap-text", children: [
      "You're ",
      formatMoney(remainingAmount),
      " away from Free Standard Shipping"
    ] }),
    /* @__PURE__ */ jsx(BorderLinearProgress, { variant: "determinate", value: total / freeShippingCap * 100 }),
    /* @__PURE__ */ jsxs(Box, { className: "free-shipping-text", children: [
      /* @__PURE__ */ jsx("div", { children: formatMoney(0) }),
      /* @__PURE__ */ jsx("div", { children: formatMoney(freeShippingCap) })
    ] })
  ] });
};
const CheckoutDrawer = ({
  loading,
  cartItems,
  showFreeShippingOffer = false,
  allCarts,
  storeBrandingType,
  cartProducts,
  handleAddQuantity,
  handleMinusQuantity,
  closeDrawer,
  onCheckout,
  onViewBag,
  onEmptyBagAction,
  LoadingComponent,
  initiating = false,
  closeAfterCheckout = true
}) => {
  var _a, _b, _c, _d;
  const hasItems = allCarts && allCarts.length > 0;
  const retailerWithItems = allCarts == null ? void 0 : allCarts.filter((cart) => cart.items.length > 0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const checkoutDrawerHeaderButton = (_b = (_a = theme.globalComponentPresets) == null ? void 0 : _a.checkoutDrawer) == null ? void 0 : _b.checkoutDrawerHeaderButton;
  const customHeaderName = (_d = (_c = theme.globalComponentPresets) == null ? void 0 : _c.checkoutDrawer) == null ? void 0 : _d.customHeaderName;
  return /* @__PURE__ */ jsxs(CheckoutContainer, { className: "bc-checkout-drawer", children: [
    /* @__PURE__ */ jsxs(Box, { className: "bc_header_section", children: [
      /* @__PURE__ */ jsx(Box, { className: "bc_header", children: customHeaderName || "Bag" }),
      /* @__PURE__ */ jsx(
        CloseButton,
        {
          style: {
            all: "unset",
            cursor: "pointer"
          },
          className: "options",
          onClick: closeDrawer,
          iconSize: 15
        }
      )
    ] }),
    showFreeShippingOffer ? /* @__PURE__ */ jsx(FreeShippingOffer, { allCarts, freeShippingCap: 35 }) : null,
    !hasItems && !loading ? /* @__PURE__ */ jsx(StyledEmptyBagMessageCard, { onActionClick: onEmptyBagAction || closeDrawer }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      hasItems && checkoutDrawerHeaderButton && isMobile ? /* @__PURE__ */ jsx(
        CheckoutSection,
        {
          canCheckout: (retailerWithItems == null ? void 0 : retailerWithItems.length) === 1,
          initiating,
          allCarts,
          closeDrawer,
          onCheckout,
          onViewBag,
          closeAfterCheckout
        }
      ) : null,
      /* @__PURE__ */ jsx(
        ProductSection,
        {
          storeBrandingType,
          cartProducts,
          handleAddQuantity,
          handleMinusQuantity,
          loading,
          allCarts,
          cartItems,
          LoadingComponent
        }
      ),
      hasItems && !(checkoutDrawerHeaderButton && isMobile) && /* @__PURE__ */ jsx(
        CheckoutSection,
        {
          canCheckout: (retailerWithItems == null ? void 0 : retailerWithItems.length) === 1,
          initiating,
          allCarts,
          closeDrawer,
          onCheckout,
          onViewBag,
          closeAfterCheckout
        }
      )
    ] })
  ] });
};
export {
  CheckoutDrawer,
  calculateTotalOfAllCarts
};
//# sourceMappingURL=CheckoutDrawer.js.map
